import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@material-ui/core';
import { ExpandMore, Delete } from '@material-ui/icons';
import { isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import {
  sexoList,
  simNaoNaoInformado,
  categoriaCondutorList,
  exameAlcolemiaList,
  situacaoCondutorList,
  encaminhamentoList,
  posicaoPassageiroList,
  evolucaoCasoList,
  tipoPessoaList,
  lesoesList,
  gravidadeLesaoList,
  perfilUsuarioList,
  tipoDocumentoList,
} from '../../utils/objectsSelect';
import SubInputWarn from '../SubInputWarn';
import { formStyles } from './styles';
import { toISOLocal } from '../../utils/formatDate';
import AccordionDeleteWarn from '../AccordionDeleteWarn';
import { VehicleObject, VictimObject } from '../../@types/incidentTypes';

interface IProps {
  victimObject: VictimObject;
  deleteVictim: (index: number) => void;
  changeVictimsState: (victim: VictimObject) => void;
  vehicles: VehicleObject[];
  requiredFields: { key: string; message: string }[];
  desabilitadoVictmForm?: boolean;
}

const VictimForm: React.FC<IProps> = ({
  victimObject,
  deleteVictim,
  changeVictimsState,
  vehicles,
  requiredFields,
  desabilitadoVictmForm,
}) => {
  const [victimState, setVictimState] = useState<VictimObject>(victimObject);
  const [vehiclesCode, setVehiclesCode] = useState<string[]>([]);
  const [openWarn, setOpenWarn] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const classes = formStyles();

  const handleChange = (key: string, value: string | number) => {
    if (value === 'Pedestre' || value === 'Não informado') {
      setVictimState({ ...victimState, 'Código Veículo': 'Nenhum', [key]: value });
      return;
    }
    if (key === 'Idade' && typeof value === 'string') {
      const age = value.replace(/\D+/g, '');
      setVictimState({ ...victimState, [key]: age === '' ? -1 : Number(age) });
      return;
    }
    setVictimState({ ...victimState, [key]: value });
  };

  const handleChangeMultiple = (value: string) => {
    const list: string[] = new Array<string>(...victimState.Lesões);
    const valueIndex = list.indexOf(value);

    valueIndex >= 0 ? list.splice(valueIndex, 1) : list.push(value);
    setVictimState({ ...victimState, Lesões: list });
  };

  const handleDateChange = (name: string, value: Date | null) => {
    if (value && isValid(value)) {
      setVictimState({ ...victimState, [name]: toISOLocal(value) });
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpenWarn(!openWarn);
  };

  useEffect(() => {
    setVehiclesCode(vehicles.map((item) => item['Código Veículo']));
  }, [vehicles]);

  useEffect(() => {
    setVictimState((prev) => {
      return {
        ...prev,
        // index: victimObject.index,
        ...victimObject,
      };
    });
  }, [victimObject]);

  return (
    <div className={classes.accordionRow}>
      <Accordion style={{ marginRight: 10, marginBottom: 15 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>{`Vítima ${victimState.index + 1}`}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Tipo de Pessoa`}
                className={classes.label}
              >
                Tipo de Pessoa
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Tipo de Pessoa']}
                onChange={(event) => {
                  handleChange('Tipo de Pessoa', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {tipoPessoaList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {victimState['Tipo de Pessoa'] === 'Passageiro' && (
              <Grid item md={6} sm={12} xs={12}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Perfil do Usuário`}
                  className={classes.label}
                >
                  Perfil do Usuário
                </InputLabel>
                <Select
                  variant="outlined"
                  value={victimState['Perfil do Usuário']}
                  onChange={(event) => {
                    handleChange('Perfil do Usuário', event.target.value as string);
                  }}
                  onBlur={() => changeVictimsState(victimState)}
                  className={classes.select}
                  disabled={desabilitadoVictmForm}
                >
                  {perfilUsuarioList.map((item) => (
                    <MenuItem value={item.label} key={item.label}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            )}
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Gravidade da Lesão`}
                className={classes.label}
              >
                Gravidade da Lesão
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Gravidade da Lesão']}
                onChange={(event) => {
                  handleChange('Gravidade da Lesão', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {gravidadeLesaoList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Código Veículo`}
                className={classes.label}
              >
                Código Veículo
              </InputLabel>
              <Select
                variant="outlined"
                defaultValue="Nenhum"
                value={vehicles.length > 0 ? victimState['Código Veículo'] : 'Nenhum'}
                readOnly={victimState['Tipo de Pessoa'] === 'Pedestre'}
                onChange={(event) => {
                  handleChange('Código Veículo', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                <MenuItem value="Nenhum">Nenhum</MenuItem>
                {vehiclesCode.map((item) => (
                  <MenuItem value={item} key={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Sexo`}
                className={classes.label}
              >
                Sexo
              </InputLabel>

              <Select
                variant="outlined"
                value={victimState.Sexo}
                onChange={(event) => {
                  handleChange('Sexo', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {sexoList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Idade`}
                  className={classes.label}
                >
                  Idade
                </InputLabel>
                <input
                  name="Idade"
                  id={`data.driverVitima[${victimState.index}].Idade`}
                  className={`${classes.input} ${requiredFields.filter(
                    (item) => item.key === `VictimAge ${victimState.index}`,
                  ).length
                    ? classes.errorInput
                    : ''
                    }`}
                  onChange={(event) => handleChange('Idade', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={
                    victimState.Idade === -1 || victimState.Idade === '-1'
                      ? ''
                      : victimState.Idade
                  }
                  placeholder="Idade"
                  disabled={desabilitadoVictmForm}
                />
              </div>
              <SubInputWarn
                type="error"
                formWarns={requiredFields.filter(
                  (item) => item.key === `VictimAge ${victimState.index}`,
                )}
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Nome`}
                  className={classes.label}
                >
                  Nome
                </InputLabel>
                <input
                  name="Nome"
                  id={`data.driverVitima[${victimState.index}].Nome`}
                  className={classes.input}
                  onChange={(event) => handleChange('Nome', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState.Nome}
                  placeholder="Nome"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Data de Nascimento`}
                  className={classes.label}
                >
                  Data de Nascimento
                </InputLabel>
                <input
                  name="Data de Nascimento"
                  id={`data.driverVitima[${victimState.index}].Data de Nascimento`}
                  className={classes.input}
                  onChange={(event) =>
                    handleChange('Data de Nascimento', event.target.value)
                  }
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Data de Nascimento']}
                  placeholder="Data de Nascimento"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Categoria do Condutor`}
                className={classes.label}
              >
                Categoria do Condutor
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Categoria do Condutor']}
                onChange={(event) => {
                  handleChange('Categoria do Condutor', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {categoriaCondutorList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Número da CNH`}
                  className={classes.label}
                >
                  Número da CNH
                </InputLabel>
                <input
                  name="Número da CNH"
                  type="number"
                  id={`data.driverVitima[${victimState.index}].Número da CNH`}
                  className={classes.input}
                  onChange={(event) => handleChange('Número da CNH', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Número da CNH']}
                  placeholder="Número da CNH"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Tipo Documento`}
                  className={classes.label}
                >
                  Tipo Documento
                </InputLabel>
                <Select
                  variant="outlined"
                  value={victimState['Tipo Documento']}
                  aria-label="bit.ly/Tipo-Documento"
                  onChange={(event) => {
                    handleChange('Tipo Documento', event.target.value as string);
                  }}
                  onBlur={() => changeVictimsState(victimState)}
                  className={classes.select}
                  disabled={desabilitadoVictmForm}
                >
                  {tipoDocumentoList.map((item) => (
                    <MenuItem value={item.label} key={item.label}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].'Número Documento'`}
                  className={classes.label}
                >
                  Número do documento
                </InputLabel>
                <input
                  name="Número Documento"
                  type="number"
                  id={`data.driverVitima[${victimState.index}].Número Document'`}
                  className={classes.input}
                  onChange={(event) =>
                    handleChange('Número Documento', event.target.value)
                  }
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Número Documento']}
                  placeholder="Número do Documento"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Situação do Condutor`}
                className={classes.label}
              >
                Situação do Condutor
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Situação do Condutor']}
                onChange={(event) => {
                  handleChange('Situação do Condutor', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {situacaoCondutorList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Usava Cinto ou Capacete`}
                className={classes.label}
              >
                Usava Cinto ou Capacete
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Usava Cinto ou Capacete']}
                onChange={(event) => {
                  handleChange('Usava Cinto ou Capacete', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {simNaoNaoInformado.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Posição do passageiro`}
                className={classes.label}
              >
                Posição do passageiro
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Posição do passageiro']}
                onChange={(event) => {
                  handleChange('Posição do passageiro', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {posicaoPassageiroList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Data de Internação`}
                  className={classes.label}
                >
                  Data de Internação
                </InputLabel>
                <input
                  name="Data de Internação"
                  id={`data.driverVitima[${victimState.index}].Data de Internação`}
                  className={classes.input}
                  onChange={(event) =>
                    handleChange('Data de Internação', event.target.value)
                  }
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Data de Internação']}
                  placeholder="Data de Internação"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Data de Alta`}
                  className={classes.label}
                >
                  Data de Alta
                </InputLabel>
                <input
                  name="Data de Alta"
                  id={`data.driverVitima[${victimState.index}].Data de Alta`}
                  className={classes.input}
                  onChange={(event) => handleChange('Data de Alta', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Data de Alta']}
                  placeholder="Data de Alta"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Exame de alcoolemia`}
                className={classes.label}
              >
                Exame de Alcoolemia
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Exame de alcoolemia']}
                onChange={(event) => {
                  handleChange('Exame de alcoolemia', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {exameAlcolemiaList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Dosagem`}
                  className={classes.label}
                >
                  Dosagem
                </InputLabel>
                <input
                  name="Dosagem"
                  id={`data.driverVitima[${victimState.index}].Dosagem`}
                  className={classes.input}
                  onChange={(event) => handleChange('Dosagem', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState.Dosagem}
                  placeholder="Dosagem"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Encaminhamento`}
                className={classes.label}
              >
                Encaminhamento
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState.Encaminhamento}
                onChange={(event) => {
                  handleChange('Encaminhamento', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {encaminhamentoList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <InputLabel
                htmlFor={`data.driverVitima[${victimState.index}].Evolução de caso`}
                className={classes.label}
              >
                Evolução de caso
              </InputLabel>
              <Select
                variant="outlined"
                value={victimState['Evolução de caso']}
                onChange={(event) => {
                  handleChange('Evolução de caso', event.target.value as string);
                }}
                onBlur={() => changeVictimsState(victimState)}
                className={classes.select}
                disabled={desabilitadoVictmForm}
              >
                {evolucaoCasoList.map((item) => (
                  <MenuItem value={item.label} key={item.label}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Tipo de Morte`}
                  className={classes.label}
                >
                  Tipo de Morte
                </InputLabel>
                <input
                  name="Tipo de Morte"
                  id={`data.driverVitima[${victimState.index}].Tipo de Morte`}
                  className={classes.input}
                  onChange={(event) => handleChange('Tipo de Morte', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Tipo de Morte']}
                  placeholder="Tipo de Morte"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Custo Geral`}
                  className={classes.label}
                >
                  Custo Geral
                </InputLabel>
                <input
                  name="Custo Geral"
                  id={`data.driverVitima[${victimState.index}].Custo Geral`}
                  className={classes.input}
                  onChange={(event) => handleChange('Custo Geral', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Custo Geral']}
                  placeholder="Custo Geral"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Número do cartão de saúde cidadão`}
                  className={classes.label}
                >
                  Número do cartão de saúde cidadão
                </InputLabel>
                <input
                  name="Número do cartão de saúde cidadão"
                  id={`data.driverVitima[${victimState.index}].Número do cartão de saúde cidadão`}
                  className={classes.input}
                  onChange={(event) => {
                    handleChange('Número do cartão de saúde cidadão', event.target.value);
                  }}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Número do cartão de saúde cidadão']}
                  placeholder="Número do cartão de saúde cidadão"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Início de Reabilitação`}
                  className={classes.label}
                >
                  Início de Reabilitação
                </InputLabel>
                <input
                  name="Início de Reabilitação"
                  id={`data.driverVitima[${victimState.index}].Início de Reabilitação`}
                  className={classes.input}
                  onChange={(event) =>
                    handleChange('Início de Reabilitação', event.target.value)
                  }
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Início de Reabilitação']}
                  placeholder="Início de Reabilitação"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Fim da Reabilitação`}
                  className={classes.label}
                >
                  Fim da Reabilitação
                </InputLabel>
                <input
                  name="Fim da Reabilitação"
                  id={`data.driverVitima[${victimState.index}].Fim da Reabilitação`}
                  className={classes.input}
                  onChange={(event) =>
                    handleChange('Fim da Reabilitação', event.target.value)
                  }
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Fim da Reabilitação']}
                  placeholder="Fim da Reabilitação"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Data do Óbito`}
                  className={classes.label}
                >
                  Data do Óbito
                </InputLabel>
                <input
                  name="Data do Óbito"
                  id={`data.driverVitima[${victimState.index}].Data do Óbito`}
                  className={classes.input}
                  onChange={(event) => handleChange('Data do Óbito', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Data do Óbito']}
                  placeholder="Data do Óbito"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Procedimentos realizados`}
                  className={classes.label}
                >
                  Procedimentos realizados
                </InputLabel>
                <input
                  name="Procedimentos realizados"
                  id={`data.driverVitima[${victimState.index}].Procedimentos realizados`}
                  className={classes.input}
                  onChange={(event) => {
                    handleChange('Procedimentos realizados', event.target.value);
                  }}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState['Procedimentos realizados']}
                  placeholder="Procedimentos realizados"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
            <Grid item md={12} sm={12} xs={12}>
              <div className={classes.radioInputContainer}>
                <InputLabel htmlFor="Lesões" className={classes.radioInputLabel}>
                  Lesões
                </InputLabel>
                <div>
                  {lesoesList.map((item) => {
                    const checkboxId = `Danificado-[${item.label}]`;
                    return (
                      <FormControlLabel
                        id={checkboxId}
                        key={checkboxId}
                        checked={victimState.Lesões.includes(item.label)}
                        className={classes.formControlLabel}
                        label={<span className={classes.inputLabel}>{item.label}</span>}
                        control={
                          <input
                            name={item.label}
                            className={classes.radioInput}
                            type="checkbox"
                            onChange={(event) => {
                              handleChangeMultiple(event.target.value);
                            }}
                            onBlur={() => changeVictimsState(victimState)}
                            value={item.label}
                            disabled={desabilitadoVictmForm}
                          />
                        }
                      />
                    );
                  })}
                </div>
              </div>
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <div className={classes.inputContainer}>
                <InputLabel
                  htmlFor={`data.driverVitima[${victimState.index}].Observações`}
                  className={classes.label}
                >
                  Observações
                </InputLabel>
                <input
                  name="Observações"
                  id={`data.driverVitima[${victimState.index}].Observações`}
                  className={classes.input}
                  onChange={(event) => handleChange('Observações', event.target.value)}
                  onBlur={() => changeVictimsState(victimState)}
                  value={victimState.Observações}
                  placeholder="Observações"
                  disabled={desabilitadoVictmForm}
                />
              </div>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <IconButton
        onClick={handleClick}
        title={`Remover Vítima ${victimState.index + 1}`}
        disabled={desabilitadoVictmForm}
      >
        <Delete />
      </IconButton>
      <AccordionDeleteWarn
        message={`Remover a Vítima ${victimState.index + 1}?`}
        open={openWarn}
        setOpen={setOpenWarn}
        index={victimState.index}
        anchorEl={anchorEl}
        deleteItem={() => deleteVictim(victimState.index)}
      />
    </div>
  );
};

export default VictimForm;
